import { useContext, useMemo } from 'react'
import GeoContext from 'contexts/geoContext'
import Places, { getPlaceByRegionCode } from 'constants/places'
import useOfferList from './useOfferList'

interface Options {
  /**
   * Max offers for the region-scoped fetch (request 1 — campaign-tagged, when `campaigns` is
   * provided; otherwise just region-scoped)
   * @default 5
   */
  regionLimit?: number
  /**
   * Max offers for the region-scoped, non-campaign-tagged backup fetch (request 2), used to fill
   * out the first `regionLimit` slots when request 1 alone doesn't return enough. Only fetched
   * when `campaigns` is provided.
   * @default 5
   */
  regionBackupLimit?: number
  /**
   * Max offers for the broader "anywhere" fetch, not restricted by destination (or campaign tag,
   * when `campaigns` is provided), used to fill out the remainder of the list
   * @default 15
   */
  anywhereLimit?: number
}

/**
 * Fetches an offer list scoped to the user's current browsing region, then tops it up with a
 * broader "anywhere" fetch so the combined list still reaches a reasonable size when there aren't
 * enough region-relevant offers on their own. Region-relevant offers are always ordered first.
 *
 * When `campaigns` is provided, this does a three-way split instead of the plain two-way one:
 * 1. Region-scoped, campaign-tagged (most relevant).
 * 2. Region-scoped, no campaign tag but restricted to offers with member pricing — backup to top
 *    up request 1 up to `regionLimit` combined offers when request 1 alone falls short.
 * 3. Anywhere, no destination or campaign restriction — fills the remainder of the list.
 * The combined region result (1 + 2, capped at `regionLimit`) is ordered first, then the anywhere
 * result. Without `campaigns`, it's just the plain two-way split (region, then anywhere), with
 * `baseFilters` applied unmodified to both — use this for carousels where dropping the campaign
 * tag as a backup isn't appropriate (e.g. member-exclusive-only carousels).
 *
 * `baseFilters` should omit `destinationId`/`limit` — this hook sets those per-request. It should
 * also omit `campaigns` when the `campaigns` param is provided (that's applied to request 1 only).
 *
 * Uses `destinationId` (not `placeIds`) because the two request paths our carousels actually hit
 * both key off `destinationId`: the hotel-specific path (getSearchLight, via
 * getPlaceIdsForSearchApi) and the tour-specific path (getTourSearchListByFacet) both read
 * `destinationId`/`destinations`, not `placeIds` — `placeIds` is only read by the generic/unified
 * search branch, which neither hotel nor tour offer types route through.
 */
export default function useRegionAwareOfferList(
  baseFilters: Omit<App.OfferListFilters, 'destinationId' | 'limit'>,
  campaigns?: Array<string>,
  { regionLimit = 5, regionBackupLimit = 5, anywhereLimit = 15 }: Options = {},
): App.OfferList {
  const { currentRegionCode } = useContext(GeoContext)
  const regionPlaceId = useMemo(() => getPlaceByRegionCode(currentRegionCode).id, [currentRegionCode])

  // Request 1: region-scoped (campaign-tagged, if campaigns is provided)
  const regionList = useOfferList({
    ...baseFilters,
    ...(campaigns ? { campaigns } : {}),
    destinationId: regionPlaceId,
    limit: regionLimit,
  })

  // Request 2: region-scoped backup, no campaign tag but restricted to offers with member pricing
  // — only fetched when campaigns is provided. Fires in parallel with request 1 (see note on the
  // anywhere fetch below for why waiting isn't necessary).
  const regionBackupList = useOfferList(
    {
      ...baseFilters,
      luxPlusFeatures: ['Member prices'],
      destinationId: regionPlaceId,
      limit: regionBackupLimit,
    },
    {
      disabled: !campaigns,
      excludedIds: regionList.offerIds,
    },
  )

  const regionCombinedIds = useMemo(() => (
    campaigns
      ? [...regionList.offerIds, ...regionBackupList.offerIds].slice(0, regionLimit)
      : regionList.offerIds
  ), [campaigns, regionList.offerIds, regionBackupList.offerIds, regionLimit])

  // Request 3: anywhere, no destination (or campaign, if campaigns is provided) restriction. Fires
  // in parallel with requests 1 and 2 rather than waiting for them — excludedIds is applied
  // client-side only (see useOfferList), so it never gates or re-triggers the request; waiting
  // would just serialize independent fetches for no correctness benefit.
  const anywhereList = useOfferList(
    {
      ...baseFilters,
      destinationId: Places.Anywhere.id,
      limit: anywhereLimit,
    },
    { excludedIds: regionCombinedIds },
  )

  return useMemo(() => ({
    ...anywhereList,
    offerIds: [...regionCombinedIds, ...anywhereList.offerIds],
    fetching: regionList.fetching || (!!campaigns && regionBackupList.fetching) || anywhereList.fetching,
  }), [regionCombinedIds, anywhereList, regionList.fetching, regionBackupList.fetching, campaigns])
}
